import React, { useState } from "react";
import { RefreshCw, Check, X, Sparkles } from "lucide-react";
import VoiceRecorder from "./VoiceRecorder";
import SidePanel from "../SidePanel";
import { SelfInterviewQuestion } from "../../services/selfInterviewService";

interface QuestionCardProps {
  question: SelfInterviewQuestion;
  index: number;
  onDismiss: (index: number) => void;
  onSwap: (index: number) => void;
  onAnswer: (index: number, answerText: string, duration?: number) => void;
  onDone: (index: number) => void;
  onAssess: (index: number) => void;
  onSharpen: (index: number) => void;
  onBuildEmail: (index: number) => void;
  isSwapping?: boolean;
  isSharpening?: boolean;
}

const SCORE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  "Bad": { bg: "#FEF2F2", text: "#DC2626", border: "#FECACA" },
  "Okay": { bg: "#FFFBEB", text: "#D97706", border: "#FDE68A" },
  "Good": { bg: "#F0FDF4", text: "#16A34A", border: "#BBF7D0" },
  "Excellent": { bg: "#EEF2FF", text: "#4F46E5", border: "#C7D2FE" },
};

const QuestionCard: React.FC<QuestionCardProps> = ({
  question, index, onDismiss, onSwap, onAnswer, onDone, onAssess, onSharpen, onBuildEmail, isSwapping, isSharpening,
}) => {
  const [showAnswer, setShowAnswer] = useState(false);
  const [answerText, setAnswerText] = useState(question.answer_text || "");
  const [feedbackPanelOpen, setFeedbackPanelOpen] = useState(false);
  const isDone = !!question.answer_text;
  const hasFeedback = question.quality_score && question.quality_feedback;
  const scoreStyle = question.quality_score ? SCORE_COLORS[question.quality_score] : null;

  const handleDone = () => {
    if (answerText.trim()) {
      onAnswer(index, answerText.trim());
      onDone(index);
      setShowAnswer(false);
      onAssess(index);
    }
  };

  if (question.dismissed) return null;

  return (
    <>
      <style>{`
        @media (max-width: 640px) {
          .si-card-row { flex-direction: column !important; }
          .si-card-row .si-rating-col { width: 100% !important; flex-direction: row !important; padding: 8px 12px !important; gap: 6px !important; }
          .si-answer-area { padding: 0 14px 14px !important; }
        }
      `}</style>
      <div className="si-card-row" style={{
        display: "flex", gap: "12px",
        marginBottom: "12px",
        alignItems: "stretch",
      }}>
        {/* LEFT COLUMN — Question card */}
        <div style={{
          flex: 1, minWidth: 0,
          border: `1px solid ${isDone ? "#BBF7D0" : "#E5E7EB"}`,
          borderRadius: "12px",
          backgroundColor: isDone ? "#FAFFF7" : "#FFFFFF",
          transition: "all 0.2s ease",
        }}>
          {/* Question content */}
          <div style={{ padding: "16px 20px" }}>
            {/* Top row: question + actions */}
            <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
              {isDone && <Check size={18} color="#16A34A" style={{ flexShrink: 0, marginTop: "2px" }} />}

              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: "14px", fontWeight: 600, color: "#111827", lineHeight: 1.4, margin: 0 }}>
                  {question.question}
                </p>

                {/* Article link for news questions */}
                {question.source === "web_search" && question.article_title && question.article_url && (
                  <a
                    href={question.article_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: "12px", color: "#6366F1", textDecoration: "none", marginTop: "6px", display: "inline-block" }}
                  >
                    {question.article_title} &rarr;
                  </a>
                )}

                {/* Answer preview when done */}
                {isDone && question.answer_text && !showAnswer && (
                  <p style={{
                    fontSize: "13px", color: "#6B7280", marginTop: "6px", margin: "6px 0 0 0",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {question.answer_text.substring(0, 120)}...
                  </p>
                )}
              </div>

              {/* Action icons */}
              <div style={{ display: "flex", gap: "4px", flexShrink: 0 }}>
                {!isDone && (
                  <button
                    onClick={() => onSwap(index)}
                    disabled={isSwapping}
                    style={{
                      padding: "6px", borderRadius: "6px", border: "none",
                      backgroundColor: "transparent", cursor: isSwapping ? "wait" : "pointer",
                      color: "#9CA3AF", opacity: isSwapping ? 0.5 : 1,
                    }}
                    title="Get a different question"
                  >
                    <RefreshCw size={16} className={isSwapping ? "spin" : ""} />
                  </button>
                )}
                <button
                  onClick={() => {
                    if (window.confirm("Are you sure you want to delete this question? This cannot be undone.")) {
                      onDismiss(index);
                    }
                  }}
                  style={{
                    padding: "6px", borderRadius: "6px", border: "none",
                    backgroundColor: "transparent", cursor: "pointer", color: "#9CA3AF",
                  }}
                  title="Delete question"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Answer / Edit / Sharpen buttons */}
            {!showAnswer && (
              <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                <button
                  onClick={() => setShowAnswer(true)}
                  style={{
                    padding: "6px 14px", borderRadius: "6px",
                    border: "1px solid #D1D5DB", backgroundColor: "#FFFFFF",
                    fontSize: "13px", color: "#374151", cursor: "pointer",
                  }}
                >
                  {isDone ? "Edit answer" : "Answer"}
                </button>
                {isDone && (
                  <button
                    onClick={() => onSharpen(index)}
                    disabled={isSharpening}
                    style={{
                      padding: "6px 14px", borderRadius: "6px",
                      border: "1px solid #D1D5DB", backgroundColor: "#FFFFFF",
                      fontSize: "13px", color: "#374151", cursor: isSharpening ? "wait" : "pointer",
                      display: "flex", alignItems: "center", gap: "4px",
                      opacity: isSharpening ? 0.7 : 1,
                    }}
                  >
                    <Sparkles size={12} /> {isSharpening ? "Sharpening..." : "Sharpen with AI"}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Answer area */}
          {showAnswer && (
            <div className="si-answer-area" style={{ padding: "0 20px 20px", borderTop: "1px solid #F3F4F6" }}>
              <div style={{ marginTop: "16px", marginBottom: "12px" }}>
                <VoiceRecorder
                  onTranscriptChange={(text) => {
                    setAnswerText(text);
                    onAnswer(index, text);
                  }}
                  initialText={answerText}
                />
              </div>
              <textarea
                value={answerText}
                onChange={(e) => {
                  setAnswerText(e.target.value);
                  onAnswer(index, e.target.value);
                }}
                placeholder="Type your answer or use voice recording above... ramble away!"
                rows={6}
                style={{
                  width: "100%", padding: "12px 14px", borderRadius: "8px",
                  border: "1px solid #D1D5DB", fontSize: "14px", lineHeight: 1.6,
                  resize: "vertical", outline: "none", fontFamily: "inherit",
                  boxSizing: "border-box",
                }}
              />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "12px" }}>
                <span style={{ fontSize: "12px", color: "#9CA3AF" }}>
                  {answerText.trim().split(/\s+/).filter(Boolean).length} words
                </span>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => setShowAnswer(false)}
                    style={{
                      padding: "8px 16px", borderRadius: "8px",
                      border: "1px solid #D1D5DB", backgroundColor: "#FFFFFF",
                      fontSize: "14px", color: "#6B7280", cursor: "pointer",
                    }}
                  >
                    Hide
                  </button>
                  <button
                    onClick={handleDone}
                    disabled={!answerText.trim()}
                    style={{
                      padding: "8px 20px", borderRadius: "8px",
                      backgroundColor: answerText.trim() ? "#6366F1" : "#E5E7EB",
                      color: answerText.trim() ? "#FFFFFF" : "#9CA3AF",
                      border: "none", fontSize: "14px", fontWeight: 600,
                      cursor: answerText.trim() ? "pointer" : "not-allowed",
                      display: "flex", alignItems: "center", gap: "6px",
                    }}
                  >
                    <Check size={16} /> Done
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN — Rating */}
        <div className="si-rating-col" style={{
          width: "90px", flexShrink: 0,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          gap: "2px", borderRadius: "12px",
          border: "1px solid #E5E7EB",
          backgroundColor: "#F9FAFB",
          padding: "12px 8px",
          cursor: hasFeedback ? "pointer" : "default",
          transition: "box-shadow 0.2s ease",
        }}
          onClick={hasFeedback ? () => setFeedbackPanelOpen(true) : undefined}
          onMouseEnter={(e) => { if (hasFeedback) e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.12)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
          title={scoreStyle
            ? (hasFeedback ? "AI rating on your response. Click to see how to improve." : "AI rating on your response")
            : "Rating appears after you answer"}
        >
          {scoreStyle && question.quality_score && !showAnswer ? (
            <>
              <span style={{ fontSize: "13px", fontWeight: 700, color: scoreStyle.text }}>
                {question.quality_score}
              </span>
              {hasFeedback && (
                <span style={{ fontSize: "10px", color: "#6B7280", textAlign: "center", marginTop: "2px" }}>
                  learn why
                </span>
              )}
            </>
          ) : isDone && !showAnswer && !question.quality_score ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
              <div style={{
                width: "16px", height: "16px", border: "2px solid #E5E7EB",
                borderTopColor: "#6366F1", borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
              }} />
              <span style={{ fontSize: "10px", color: "#9CA3AF" }}>rating</span>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : (
            <span style={{ fontSize: "11px", color: "#9CA3AF" }}>—</span>
          )}
        </div>
      </div>

      {/* Feedback side panel */}
      <SidePanel isOpen={feedbackPanelOpen} onClose={() => setFeedbackPanelOpen(false)}>
        <h3 style={{ fontSize: "18px", fontWeight: 700, color: "#111827", marginBottom: "16px" }}>
          Response Rating
        </h3>
        <p style={{ fontSize: "14px", fontWeight: 600, color: "#374151", marginBottom: "12px" }}>
          {question.question}
        </p>
        {scoreStyle && question.quality_score && (
          <span style={{
            fontSize: "12px", fontWeight: 600,
            padding: "4px 12px", borderRadius: "99px",
            backgroundColor: scoreStyle.bg, color: scoreStyle.text,
            border: `1px solid ${scoreStyle.border}`,
            display: "inline-block", marginBottom: "16px",
          }}>
            {question.quality_score}
          </span>
        )}
        <h4 style={{ fontSize: "14px", fontWeight: 600, color: "#374151", marginBottom: "8px" }}>
          {question.quality_score === "Excellent" ? "Why this is excellent" : question.quality_score === "Good" ? "What would make this excellent" : "How to improve"}
        </h4>
        <p style={{ fontSize: "14px", color: "#6B7280", lineHeight: 1.7, marginBottom: "16px" }}>
          {question.quality_feedback}
        </p>
        {question.quality_suggestions && question.quality_suggestions.length > 0 && (
          <>
            <h4 style={{ fontSize: "14px", fontWeight: 600, color: "#374151", marginBottom: "8px" }}>
              Try this
            </h4>
            <ul style={{ margin: 0, paddingLeft: "20px" }}>
              {question.quality_suggestions.map((s, i) => (
                <li key={i} style={{ fontSize: "14px", color: "#6B7280", lineHeight: 1.7, marginBottom: "8px" }}>
                  {s}
                </li>
              ))}
            </ul>
          </>
        )}
      </SidePanel>
    </>
  );
};

export default QuestionCard;
